/* eslint-disable */
import { ref, inject, nextTick, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCart } from "@/composeables/useCart";
import cookiesService from "@/services/cookiesService";
import makercartActivityService from "@/services/makercartActivityService";
import StemStudioService from "@/services/stemStudioService";
import { getApiError } from "@/utils/apiError";

export function useMakerCartModals() {
  const route = useRoute();
  const router = useRouter();
  const notify = inject("notify");

  // Modal refs
  const makerCartDetail = ref(null);
  const userCart = ref(null);
  const confirmationModal = ref(null);
  const successModal = ref(null);
  const minimumRequestModal = ref(null);
  const makercartDetailModal = ref(null);

  // Modal state
  const showLoginRequiredModal = ref(false);
  const showViewLimitModal = ref(false);
  const showMakercartDetailModal = ref(false);
  const selectedMakercartId = ref(null);
  const limitResourceLeft = ref(3);
  const isUserLogged = ref(false);
  const isAuthenticated = ref(false);

  // Use cart composable
  const {
    cartItems,
    clearCart,
    shouldAutoProcessCart,
    setAutoProcessCart,
    makercarts,
    signatureMakercarts,
    singleActivities,
  } = useCart();

  // Computed properties for cart display
  const hasCartWithActivities = computed(() => {
    const hasRegularCarts = makercarts.value.some(
      (cart) => cart.activities && cart.activities.length > 0
    );
    const hasSignatureCarts = signatureMakercarts.value.some(
      (cart) => cart.activities && cart.activities.length > 0
    );
    const hasSingleActivities =
      singleActivities.value && singleActivities.value.length > 0;
    return hasRegularCarts || hasSignatureCarts || hasSingleActivities;
  });

  const displayedCarts = computed(() => {
    const cartsWithActivities = makercarts.value.filter(
      (cart) => cart.activities && cart.activities.length > 0
    );
    return cartsWithActivities.slice(0, 2);
  });

  const remainingCartsCount = computed(() => {
    const cartsWithActivities = makercarts.value.filter(
      (cart) => cart.activities && cart.activities.length > 0
    );
    return Math.max(0, cartsWithActivities.length - 2);
  });

  // Computed redirect path for login modal
  const computedRedirectPath = computed(() => {
    try {
      const basePath = route.path || "/";
      const url = new URL(window.location.origin + basePath);
      const query = { ...route.query };
      url.search = new URLSearchParams({ ...query, autoCart: "1" }).toString();
      return url.pathname + url.search;
    } catch (e) {
      return route.fullPath || route.path || "/";
    }
  });

  // Modal handlers
  const updateVisibilityModalMakerCartDetail = (visible, id) => {
    if (visible && id) {
      const query = { ...route.query };
      query.item = id;
      router.replace({ query });
    }

    if (makerCartDetail.value?.updateVisibilityModal) {
      makerCartDetail.value.updateVisibilityModal(visible, id);
    }
  };

  const handleModalClosed = () => {
    const query = { ...route.query };
    delete query.item;
    router.replace({ query });
  };

  const updateVisibilityMakercartDetailModal = (
    visible,
    makercartId = null
  ) => {
    showMakercartDetailModal.value = visible;
    selectedMakercartId.value = makercartId;

    if (visible && makercartId) {
      const query = { ...route.query };
      query.item = makercartId;
      router.replace({ query });
    }
  };

  const handleMakercartDetailModalClose = () => {
    showMakercartDetailModal.value = false;
    selectedMakercartId.value = null;

    const query = { ...route.query };
    delete query.item;
    router.replace({ query });
  };

  const handleMakercartModalAddToCart = (data) => {
    console.log("Item added to cart from new modal:", data);
  };

  const handleItemClicked = (makercartId) => {
    const useNewModal = true;
    if (useNewModal) {
      updateVisibilityMakercartDetailModal(true, makercartId);
    } else {
      updateVisibilityModalMakerCartDetail(true, makercartId);
    }
  };

  const updateVisibilityModalUserCart = (visible) => {
    if (userCart.value?.updateVisibilityModal) {
      userCart.value.updateVisibilityModal(visible);
    }
  };

  const updateVisibilityModalConfirmation = (visible) => {
    if (cartItems.value.length < 4) {
      if (minimumRequestModal.value?.updateVisibilityModal) {
        minimumRequestModal.value.updateVisibilityModal(true);
      }
      return;
    }

    if (confirmationModal.value?.updateVisibilityModal) {
      confirmationModal.value.updateVisibilityModal(visible);
    }
  };

  const updateVisibilityModalSuccess = () => {
    postCartData();
  };

  const closeAllPopup = () => {
    if (userCart.value?.updateVisibilityModal) {
      userCart.value.updateVisibilityModal(false);
    }
    if (confirmationModal.value?.updateVisibilityModal) {
      confirmationModal.value.updateVisibilityModal(false);
    }
  };

  const updateVisibilityModalLogin = (visible) => {
    if (visible) {
      setAutoProcessCart(true);
      try {
        localStorage.setItem("autoProcessCart", "1");
        const query = { ...route.query };
        delete query.autoCart;
        router.replace({ query });

        const basePath = route.path || "/";
        const url = new URL(window.location.origin + basePath);
        const nextQuery = { ...query, autoCart: "1" };

        url.search = new URLSearchParams(nextQuery).toString();
        localStorage.setItem("postLoginRedirect", url.pathname + url.search);
      } catch (e) {
        console.error("Error updating visibility modal login:", e);
      }
    }
    showLoginRequiredModal.value = visible;
  };

  const handleLoginModalClose = () => {
    showLoginRequiredModal.value = false;
  };

  const handleUserAuthenticated = (data) => {
    console.log("User authenticated from ViewLimitModal:", data);
    isAuthenticated.value = true;
    showViewLimitModal.value = false;
  };

  const handleConfirm = () => {
    updateVisibilityModalSuccess(true);
  };

  const handleCancel = () => {
    console.log("Modal canceled");
  };

  // Cart data posting
  const postCartData = async () => {
    try {
      if (cartItems.value.length === 0) {
        console.log("No items in the cart.");
        if (confirmationModal.value?.setLoading) {
          confirmationModal.value.setLoading(false);
        }
        if (confirmationModal.value?.enableButtons) {
          confirmationModal.value.enableButtons();
        }
        notify.warning("Cart is empty");
        return;
      }

      const data = {
        items: cartItems.value.map((item) => ({
          stem_makercart_id: item.id,
          qty: 1,
          grade: item.grade,
          focus: item.focus,
          number_of_students: item.numberOfStudents,
        })),
      };

      if (confirmationModal.value?.setLoading) {
        confirmationModal.value.setLoading(true);
      }
      if (confirmationModal.value?.disableButtons) {
        confirmationModal.value.disableButtons();
      }

      const response = await StemStudioService.post(data);

      if (response.status === 200) {
        clearCart();
        if (successModal.value?.updateVisibilityModal) {
          successModal.value.updateVisibilityModal(true);
        }
        if (confirmationModal.value?.setLoading) {
          confirmationModal.value.setLoading(false);
        }
        if (confirmationModal.value?.enableButtons) {
          confirmationModal.value.enableButtons();
        }
        closeAllPopup();
        notify.success("Order submitted successfully!");
      } else if (response.status === 401) {
        if (confirmationModal.value?.setLoading) {
          confirmationModal.value.setLoading(false);
        }
        if (confirmationModal.value?.enableButtons) {
          confirmationModal.value.enableButtons();
        }
        closeAllPopup();
        updateVisibilityModalLogin(true);
      } else {
        throw new Error("Failed to submit order");
      }
    } catch (err) {
      const { message, code } = getApiError(
        err,
        "Failed to submit order. Please try again."
      );
      console.error("Error posting cart data:", message, code);
      notify.error(message);
      if (confirmationModal.value?.setLoading) {
        confirmationModal.value.setLoading(false);
      }
      if (confirmationModal.value?.enableButtons) {
        confirmationModal.value.enableButtons();
      }
    }
  };

  // View limit handling
  const handleViewLimitReached = async () => {
    try {
      const limitResponse = await makercartActivityService.getLimitCounter();
      if (limitResponse.status === 200) {
        limitResourceLeft.value = 3 - limitResponse.data.data.count;
      }
    } catch (error) {
      console.error("Failed to fetch limit counter:", error);
    }

    showViewLimitModal.value = true;
  };

  // Auth checking
  const checkAuthToken = async () => {
    try {
      const authToken = await cookiesService.getCookie("authToken");
      if (authToken) {
        isUserLogged.value = true;

        const autoFlag = (() => {
          try {
            return localStorage.getItem("autoProcessCart") === "1";
          } catch (e) {
            return false;
          }
        })();

        const shouldAuto =
          (shouldAutoProcessCart.value || autoFlag) &&
          route.query.autoCart === "1";

        if (shouldAuto) {
          setAutoProcessCart(false);
          try {
            localStorage.removeItem("autoProcessCart");
          } catch (e) {
            console.error("Error removing auto process cart:", e);
          }

          const cleanedQuery = { ...route.query };
          delete cleanedQuery.autoCart;
          router.replace({ query: cleanedQuery });

          if (cartItems.value.length >= 4) {
            await nextTick();
            if (userCart.value?.updateVisibilityModal) {
              userCart.value.updateVisibilityModal(true);
            }
            if (confirmationModal.value?.updateVisibilityModal) {
              confirmationModal.value.updateVisibilityModal(true);
            }
            if (confirmationModal.value?.triggerConfirm) {
              confirmationModal.value.triggerConfirm();
            }
          } else {
            notify.warning("Cart must have at least 4 items to process order");
            await nextTick();
            if (minimumRequestModal.value?.updateVisibilityModal) {
              minimumRequestModal.value.updateVisibilityModal(true);
            }
          }
        }
      }
    } catch (err) {
      console.error(err);
      notify.error("Authentication check failed");
    }
  };

  const updateLikedStatus = ({ id, liked }) => {
    // This will be handled by the parent component
    return { id, liked };
  };

  // Track if any modal is open
  const isAnyModalOpen = computed(() => {
    return (
      showMakercartDetailModal.value ||
      showLoginRequiredModal.value ||
      showViewLimitModal.value
    );
  });

  return {
    // Refs
    makerCartDetail,

    userCart,
    confirmationModal,
    successModal,
    minimumRequestModal,
    makercartDetailModal,

    // State
    showLoginRequiredModal,
    showViewLimitModal,
    showMakercartDetailModal,
    selectedMakercartId,
    limitResourceLeft,
    isUserLogged,
    isAuthenticated,

    // Computed
    hasCartWithActivities,
    displayedCarts,
    remainingCartsCount,
    computedRedirectPath,
    signatureMakercarts,
    isAnyModalOpen,

    // Methods
    updateVisibilityModalMakerCartDetail,
    handleModalClosed,
    updateVisibilityMakercartDetailModal,
    handleMakercartDetailModalClose,
    handleMakercartModalAddToCart,
    handleItemClicked,
    updateVisibilityModalUserCart,
    updateVisibilityModalConfirmation,
    updateVisibilityModalSuccess,
    closeAllPopup,
    updateVisibilityModalLogin,
    handleLoginModalClose,
    handleUserAuthenticated,
    handleConfirm,
    handleCancel,
    postCartData,
    handleViewLimitReached,
    checkAuthToken,
    updateLikedStatus,
  };
}
